/*
 * cobbler.fit — Homepage A, "Deconstruct" hero.
 *
 * A scroll-driven 3D stage: one shoe turns slowly and, as the visitor scrolls,
 * comes apart into a labelled exploded view — laces, upper panels, welt,
 * lining, two footbeds, the Vibram sole, and the hand stitches as a field of
 * stars — before resolving into a CTA.
 *
 * Play: a button on the stage (and the "P" key) holds the explosion open and
 * sets everything orbiting the shoe — each layer on its own ring, the stitches
 * swirling round it — and lets you drag to turn it. Esc / the button again
 * hands it back to the scroll.
 *
 * The shoe is loaded, painted and fitted by CfHome3D (assets/cf-home-3d.js),
 * the same rules as the Vitrine and the product page: a VitrineData colourway
 * by id, fitSoleToUpper, the leather lining from liningOptions.
 *
 * Boots on DOMContentLoaded for every [data-cf-deconstruct]; config on data-*.
 */
(function (w, d) {
  'use strict';

  function clamp(v, lo, hi) { return v < lo ? lo : v > hi ? hi : v; }
  function lerp(a, b, t) { return a + (b - a) * t; }
  function smoothstep(e0, e1, x) {
    var t = clamp((x - e0) / (e1 - e0 || 1e-6), 0, 1);
    return t * t * (3 - 2 * t);
  }

  function init(root) {
    var K = w.CfHome3D, THREE = w.THREE;
    var canvas = root.querySelector('[data-deco-canvas]');
    var stage = root.querySelector('[data-deco-stage]');
    var labelWrap = root.querySelector('[data-deco-labels]');
    var playBtns = root.querySelectorAll('[data-deco-play]');
    var shoeId = root.getAttribute('data-model') || 'club';

    if (!K || !K.ok() || !canvas || !stage || !K.hasWebGL() || K.reducedMotion()) {
      root.classList.add('cf-deco--static');
      return;
    }
    var DEG = Math.PI / 180;

    var renderer = new THREE.WebGLRenderer({ canvas: canvas, antialias: true, alpha: true });
    renderer.setPixelRatio(Math.min(w.devicePixelRatio || 1, 1.8));
    renderer.setClearAlpha(0);
    renderer.outputEncoding = THREE.sRGBEncoding;
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;

    var scene = new THREE.Scene();
    var camera = new THREE.PerspectiveCamera(38, 1, 0.01, 100);
    var camBase = new THREE.Vector3(2.4, 0.62, 2.5);
    var viewDir = camBase.clone().normalize();
    var assembledDist = camBase.length();

    scene.add(new THREE.AmbientLight(0xffffff, 0.46));
    var key = new THREE.DirectionalLight(0xfff2e2, 2.0);
    key.position.set(2.4, 4.2, 2.6);
    key.castShadow = true;
    key.shadow.mapSize.set(1024, 1024);
    key.shadow.camera.near = 0.1; key.shadow.camera.far = 18;
    key.shadow.camera.left = -3; key.shadow.camera.right = 3;
    key.shadow.camera.top = 3; key.shadow.camera.bottom = -3;
    key.shadow.bias = -0.0012; key.shadow.radius = 7;
    scene.add(key);
    var fill = new THREE.DirectionalLight(0xdfeaff, 0.42); fill.position.set(-3.2, 1.0, -1.5); scene.add(fill);
    var rim = new THREE.DirectionalLight(0xffd9b0, 0.9); rim.position.set(-1.0, 1.6, -3.2); scene.add(rim);

    var ground = new THREE.Mesh(new THREE.PlaneGeometry(14, 14), new THREE.ShadowMaterial({ opacity: 0.26 }));
    ground.rotation.x = -Math.PI / 2;
    ground.receiveShadow = true;
    scene.add(ground);

    // wrapper (scale, turn) → tilt (play drag pitch) → shoe root (centred).
    var wrapper = new THREE.Group();
    var tilt = new THREE.Group();
    wrapper.add(tilt);
    scene.add(wrapper);

    var rig = null, scale = 1, boxCY = 0, labels = [];

    function addLabel(pivot, text) {
      if (!labelWrap) return;
      var el = d.createElement('div');
      el.className = 'cf-deco-label';
      el.innerHTML = '<span class="cf-deco-label-dot"></span><span class="cf-deco-label-txt"></span>';
      el.lastChild.textContent = text;
      labelWrap.appendChild(el);
      labels.push({ el: el, pivot: pivot });
    }

    // The shoe: the pair picked in the closet above (CfHome3D.getPick), else a
    // random one off its shelf, else this section's own model/colourway. Built
    // as the section comes within a screen of view — after the closet has had
    // its turn at the network — and rebuilt if the pick changed while it was
    // off-screen.
    var pickEl = root.querySelector('[data-deco-pick]');
    var shown = null, current = null, building = false, near = false, onScreen = false;
    function own() {
      return {
        shoeId: shoeId, glb: root.getAttribute('data-glb'),
        colorwayId: root.getAttribute('data-colorway'), title: '', chosen: false
      };
    }
    function keyOf(p) { return p ? p.shoeId + '|' + p.colorwayId : ''; }
    function wanted() { return (K.getPick && K.getPick()) || own(); }

    function clearShoe() {
      if (current) { tilt.remove(current.root); current = null; }
      rig = null;
      labels.forEach(function (l) { if (l.el.parentNode) l.el.parentNode.removeChild(l.el); });
      labels = [];
    }

    // A closet on the page loads first; this waits for it to be stocked.
    var waitCloset = !!d.querySelector('[data-cf-closet]:not(.cf-closet--static)') && !w.__cfClosetStocked;
    w.addEventListener('cf:closet-stocked', function () { waitCloset = false; maybeBuild(); });
    setTimeout(function () { waitCloset = false; maybeBuild(); }, 12000);

    // Build when near; never swap a shown shoe while the stage is in view.
    function maybeBuild() {
      if (!near) return;
      if (waitCloset && !onScreen) return;
      if (shown && onScreen) return;
      build();
    }

    function build() {
      var spec = wanted();
      if (building || keyOf(spec) === keyOf(shown)) return;
      building = true;
      root.classList.remove('cf-deco--ready');
      K.buildShoe({
        shoeId: spec.shoeId,
        shoeUrl: spec.glb,
        soleUrl: root.getAttribute('data-sole'),
        draco: root.getAttribute('data-draco'),
        colorwayId: spec.colorwayId,
        liningId: root.getAttribute('data-lining') || 'leather'
      }).then(function (shoe) {
        building = false;
        clearShoe();
        current = shoe;
        shown = spec;
        var size = shoe.box.getSize(new THREE.Vector3());
        var c = shoe.box.getCenter(new THREE.Vector3());
        boxCY = c.y;
        shoe.root.position.set(-c.x, -c.y, -c.z);
        scale = 1.45 / (Math.max(size.x, size.y, size.z) || 1);
        wrapper.scale.setScalar(scale);
        tilt.add(shoe.root);
        shoe.root.traverse(function (o) { if (o.isMesh) o.receiveShadow = true; });

        rig = K.Rig(shoe, { liningLabel: root.getAttribute('data-lining-label') || undefined });
        rig.layout();
        rig.labels.forEach(function (l) { addLabel(l.pivot, l.text); });
        shoe.root.traverse(function (o) { if (o.isMesh && o.name !== 'cf-starfield') o.castShadow = true; });

        ground.position.y = (shoe.box.min.y - c.y) * scale - 0.02;
        if (pickEl) {
          var cw = (shoe.colorway && shoe.colorway.label) || '';
          var name = spec.title || '';
          pickEl.textContent = name ? ((spec.chosen ? 'Your pick — ' : 'Off the shelf — ') + name + (cw ? ' in ' + cw : '')) : '';
          pickEl.hidden = !name;
        }
        root.classList.add('cf-deco--ready');
        looper.wake();
        // A newer pick may have come in while this one built.
        if (keyOf(wanted()) !== keyOf(shown)) maybeBuild();
      }).catch(function (err) {
        building = false;
        console.error('[cf-deco] model load failed', err);
        if (!current) root.classList.add('cf-deco--static');
      });
    }

    if (typeof IntersectionObserver !== 'undefined') {
      new IntersectionObserver(function (es) {
        near = es[0].isIntersecting;
        maybeBuild();
      }, { rootMargin: '0px 0px 100% 0px' }).observe(root);
      // "In view" = a third of the stage showing (the closet above overlaps
      // its first few dozen pixels).
      new IntersectionObserver(function (es) {
        onScreen = es[0].intersectionRatio > 0.3;
        maybeBuild();
      }, { threshold: [0, 0.3, 0.6] }).observe(stage);
    } else {
      build();
    }
    w.addEventListener('cf:home-pick', maybeBuild);

    // ── sizing ──────────────────────────────────────────────────────────────
    var vw = 1, vh = 1;
    function resize() {
      vw = stage.clientWidth; vh = stage.clientHeight;
      if (!vw || !vh) return;
      renderer.setSize(vw, vh, false);
      camera.aspect = vw / vh;
      camera.updateProjectionMatrix();
    }
    w.addEventListener('resize', resize);
    if (typeof ResizeObserver !== 'undefined') new ResizeObserver(resize).observe(stage);
    resize();

    function progress() {
      var r = root.getBoundingClientRect();
      var span = r.height - vh;
      if (span <= 0) return 0;
      return clamp(-r.top / span, 0, 1);
    }

    // ── play ────────────────────────────────────────────────────────────────
    var playing = false, playT = 0, playBlend = 0;
    var yaw = 0, pitch = 0, yawV = 0, dragging = false, lastX = 0, lastY = 0;
    function setPlay(on) {
      playing = !!on;
      root.classList.toggle('cf-deco--playing', playing);
      for (var i = 0; i < playBtns.length; i++) {
        playBtns[i].setAttribute('aria-pressed', playing ? 'true' : 'false');
      }
      looper.wake();
    }
    for (var bi = 0; bi < playBtns.length; bi++) {
      playBtns[bi].addEventListener('click', function () { setPlay(!playing); });
    }
    d.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && playing) setPlay(false);
    });
    // Drag to turn — only while playing, so scrolling the page stays the
    // page's on touch.
    canvas.addEventListener('pointerdown', function (e) {
      if (!playing) return;
      dragging = true; lastX = e.clientX; lastY = e.clientY;
      try { canvas.setPointerCapture(e.pointerId); } catch (er) {}
    });
    canvas.addEventListener('pointermove', function (e) {
      if (!dragging) return;
      var dx = e.clientX - lastX, dy = e.clientY - lastY;
      lastX = e.clientX; lastY = e.clientY;
      yawV = dx * 0.006;
      yaw += yawV;
      pitch = clamp(pitch + dy * 0.004, -0.5, 0.6);
    });
    function endDrag() { dragging = false; }
    canvas.addEventListener('pointerup', endDrag);
    canvas.addEventListener('pointercancel', endDrag);

    // ── copy chapters ─────────────────────────────────────────────────────────
    function chEl(name) { return root.querySelector('[data-ch="' + name + '"]'); }
    var beats = [
      { el: chEl('intro'), inA: -1, inB: 0, outA: 0.05, outB: 0.11, cta: false },
      { el: chEl('fit'), inA: 0.17, inB: 0.24, outA: 0.31, outB: 0.37, cta: false },
      { el: chEl('design'), inA: 0.44, inB: 0.51, outA: 0.58, outB: 0.64, cta: false },
      { el: chEl('step'), inA: 0.70, inB: 0.76, outA: 0.83, outB: 0.88, cta: false },
      { el: chEl('finale'), inA: 0.92, inB: 0.97, outA: 2, outB: 3, cta: true }
    ];
    function setChapter(el, a, dy, cta) {
      if (!el) return;
      el.style.opacity = a;
      el.style.transform = 'translateY(' + dy + 'px)';
      el.style.pointerEvents = (cta && a > 0.5) ? 'auto' : 'none';
    }

    // ── frame ─────────────────────────────────────────────────────────────────
    var pv = new THREE.Vector3();
    var looper = K.loop(root, function (dt, t) {
      if (!vw || !vh) return;
      var p = progress();
      var ex = smoothstep(0.06, 0.86, p);
      var labelA = smoothstep(0.50, 0.60, p) * (1 - smoothstep(0.86, 0.93, p));

      playBlend = clamp(playBlend + (playing ? dt : -dt) / 0.9, 0, 1);
      var pb = smoothstep(0, 1, playBlend);
      if (playing || pb > 0) playT += dt;
      if (!dragging) { yawV *= 0.94; yaw += yawV; }

      wrapper.rotation.y = t * 0.06 + p * 1.05 - 0.35 + yaw * pb;
      tilt.rotation.x = pitch * pb;

      if (rig) rig.update(ex, pb, playT);

      // Camera: assembled 3/4 → the exploded stack → the orbits in play.
      var tanV = Math.tan((camera.fov * DEG) / 2);
      var dist = assembledDist, tgtY = 0;
      if (rig && rig.frame) {
        var f = rig.frame, cy = boxCY;
        var top = (f.top - cy) * scale, bottom = (f.bottom - cy) * scale;
        var distV = ((top - bottom) * 0.5 * 1.6) / tanV;
        var distH = (f.radius * scale * 1.2) / (tanV * camera.aspect);
        dist = lerp(assembledDist, Math.max(distV, distH), ex);
        tgtY = lerp(0, (top + bottom) / 2, ex);
        // Fit the orbits (seen at a shallow angle, so the rings read as
        // ellipses: their height is well under their width).
        var R = rig.playRadius * scale;
        var distP = Math.max((R * 0.62) / tanV, (R * 1.08) / (tanV * camera.aspect));
        dist = lerp(dist, distP, pb);
        tgtY = lerp(tgtY, 0, pb);
      }
      camera.position.copy(viewDir).multiplyScalar(dist);
      camera.position.y += tgtY + dist * 0.32 * pb;   // look down on the rings in play
      camera.lookAt(0, tgtY, 0);

      ground.material.opacity = 0.26 * (1 - Math.max(ex, pb));

      var la = labelA * (1 - pb);
      for (var j = 0; j < labels.length; j++) {
        var lb = labels[j];
        lb.pivot.getWorldPosition(pv);
        pv.project(camera);
        var st = lb.el.style;
        st.opacity = pv.z < 1 ? la : 0;
        st.transform = 'translate(' + ((pv.x * 0.5 + 0.5) * vw).toFixed(1) + 'px,' + ((-pv.y * 0.5 + 0.5) * vh).toFixed(1) + 'px)';
      }

      for (var bi2 = 0; bi2 < beats.length; bi2++) {
        var bt = beats[bi2];
        var aIn = smoothstep(bt.inA, bt.inB, p);
        var a = aIn * (1 - smoothstep(bt.outA, bt.outB, p)) * (1 - pb);
        setChapter(bt.el, a, (1 - aIn) * 18, bt.cta);
      }

      renderer.render(scene, camera);
    });
  }

  function boot() {
    var roots = d.querySelectorAll('[data-cf-deconstruct]');
    for (var i = 0; i < roots.length; i++) {
      try { init(roots[i]); }
      catch (e) { console.error('[cf-deco] init failed', e); roots[i].classList.add('cf-deco--static'); }
    }
  }

  if (d.readyState === 'loading') d.addEventListener('DOMContentLoaded', boot);
  else boot();
})(window, document);
